import { Component, inject } from '@angular/core';
import { AppUser, AuthService, EMPTY } from '@app/components/login.component';
import { TreeViewProviderDirective } from '@app/providers';
import { ItemWithSelection } from '@app/shared/models/item-with-selection';
import {
  isJedinicaGradiva,
  JedinicaGradiva
} from '@app/shared/models/jedinica-gradiva';
import { isDeleteActionDisabled } from '@app/shared/util/jedinica-gradiva-security-utils';
import {
  ActionComponentNew,
  ActionResult,
  ListViewProviderDirective,
  NotificationService,
  NtapGlobalConstants,
  Util
} from '@ntap/core';
import { ConfirmationService } from 'primeng/api';
import { Observable, combineLatest, first, map, mergeMap, tap } from 'rxjs';

@Component({
  selector: 'app-delete-action',
  templateUrl: './delete-action.component.html',
  styleUrls: ['./delete-action.component.scss']
})
export class DeleteActionComponent extends ActionComponentNew<ActionResult> {
  confirmationService = inject(ConfirmationService);
  listViewProvider = inject(ListViewProviderDirective);
  key = 'delete' + this.toString();

  trajnoChecked = false;

  viewName: string;
  selectedAll = false;

  user$: Observable<AppUser | EMPTY>;

  hideTrajno = false;

  constructor(
    protected override notificationService: NotificationService,
    public authService: AuthService
  ) {
    super(notificationService);

    this.user$ = this.authService.user$;
  }

  protected override initDisabledObservable(): void {
    this.isDisabled$ = combineLatest([
      this.listViewProvider.selectedItems$,
      this.user$
    ]).pipe(
      map(([selectedItems, user]) => {
        if (selectedItems.length == 0) {
          return true;
        } else if (isJedinicaGradiva(selectedItems[0])) {
          this.hideTrajno = (selectedItems as JedinicaGradiva[]).every(
            (j) => j['en~jedinica_gradiva~nadredena_jedinica_gradiva'] == null
          );
          return isDeleteActionDisabled(
            selectedItems as JedinicaGradiva[],
            user
          );
        }
        return false;
      })
    );
  }

  protected doExecute() {
    return this.getMessage().pipe(
      tap((message) => {
        this.confirmationService.confirm({
          message: message,
          key: this.key,
          accept: () => {
            this.executeOnServer().subscribe();
          }
        });
      }),
      map(() => {
        return { $infoMessage: { msg: '', title: '' } };
      })
    );
  }

  protected getActionParams(): Observable<
    ItemWithSelection & { $trajno: boolean }
  > {
    const actionParams = this.listViewProvider.selectedItems$.pipe(
      first(),
      map((selected) => {
        const params: ItemWithSelection & { $trajno: boolean } = Object.assign(
          {},
          {
            $trajno: this.trajnoChecked,
            $selected: Util.mapToBasicItemData(selected),
            $listName: this.listViewProvider.viewName,
            $filterCriteria: (this.listViewProvider?.viewState as any)?.[
              '$filterCriteria'
            ]
          }
        );
        return params;
      })
    );
    return actionParams;
  }

  getMessage() {
    return this.listViewProvider.selectedItems$.pipe(
      first(),
      map((selected) => {
        const firstSelected: any = selected[0];
        const itemType = firstSelected[NtapGlobalConstants.ATTR_$itemType];
        const length = selected.length;
        switch (itemType) {
          // TODO napraviti preko Angulara podrske za detekciju kardinalnosti objekta i poruke ?
          case 'en~jedinica_gradiva': {
            const jedinice = length == 1 ? 'jedinicu' : 'jedinice';
            return `Jeste li sigurni da želite ${this.getMode()} obrisati ${jedinice} gradiva uključujući i sve jedinice gradiva na nižim razinama?`;
          }
          case 'en~pojavni_oblik': {
            const pojavni = length == 1 ? 'pojavni oblik' : 'pojavne oblike';
            return `Jeste li sigurni da želite  ${this.getMode()} obrisati ${pojavni} ?`;
          }
          case 'en~fizicki_objekt': {
            const fizicki = length == 1 ? 'fizički objekt' : 'fizičke objekte';
            return `Jeste li sigurni da želite  ${this.getMode()} obrisati ${fizicki} ?`;
          }
          case 'en~datoteka': {
            const datoteka = length == 1 ? 'datoteku' : 'datoteke';
            return `Jeste li sigurni da želite ${this.getMode()} obrisati ${datoteka} ?`;
          }
          default:
            return `Jeste li sigurni da želite ${this.getMode()} obrisati označene stavke?`;
        }
      })
    );
  }

  getMode() {
    return this.trajnoChecked ? 'trajno' : 'rezidualno';
  }

  executeOnServer() {
    return this.getActionParams().pipe(
      mergeMap((params: any) => {
        return this.listViewProvider
          .executeAction(this.action.name, params)
          .pipe(
            tap(() => {
              if (
                !!this.listViewProvider &&
                this.listViewProvider instanceof TreeViewProviderDirective
              ) {
                this.listViewProvider.removeSelectedNodes();
              } else if (
                !!this.listViewProvider &&
                this.listViewProvider instanceof ListViewProviderDirective
              ) {
                this.listViewProvider.refresh();
              }
            })
          );
      })
    );
  }
}
